import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, ExpandLess, Settings, Check } from '@mui/icons-material';

const CookieConsent = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMandatory, setIsMandatory] = useState(false);

    // Default Preferences
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true and disabled
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        const consent = localStorage.getItem('oyeCookieConsent');
        if (!consent) {
            // Show banner after short delay
            const showTimer = setTimeout(() => setIsVisible(true), 1500);

            // Enforce mandatory choice after 20 seconds
            const mandatoryTimer = setTimeout(() => {
                if (!localStorage.getItem('oyeCookieConsent')) {
                    setIsMandatory(true);
                }
            }, 20000);

            return () => {
                clearTimeout(showTimer);
                clearTimeout(mandatoryTimer);
            };
        } else {
            try {
                const savedPrefs = JSON.parse(localStorage.getItem('oyeCookiePreferences'));
                if (savedPrefs) setPreferences(prev => ({ ...prev, ...savedPrefs }));
            } catch (e) { console.error("Error parsing cookie prefs", e); }
        }
    }, []);

    const saveConsent = (prefs) => {
        localStorage.setItem('oyeCookieConsent', 'true');
        localStorage.setItem('oyeCookiePreferences', JSON.stringify(prefs));

        // TODO: GDPR/DPDP Compliance - Consent Audit Trail
        // Ideally, send this data to your backend to maintain a legal record of consent.
        /*
        fetch('/api/v1/compliance/log-consent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                consentId: crypto.randomUUID(),
                timestamp: new Date().toISOString(),
                preferences: prefs,
                userAgent: navigator.userAgent,
                policyVersion: '1.0'
            })
        }).catch(err => console.error("Consult Log Error:", err));
        */

        setIsVisible(false);
        setIsExpanded(false);
        setIsMandatory(false);
    };

    const handleAcceptAll = () => {
        const allEnabled = { necessary: true, analytics: true, marketing: true };
        setPreferences(allEnabled);
        saveConsent(allEnabled);
    };

    const handleSavePreferences = () => {
        saveConsent(preferences);
    };

    const togglePreference = (key) => {
        if (key === 'necessary') return;
        setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <>
                    {/* Mandatory Backdrop */}
                    {isMandatory && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
                        />
                    )}

                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 flex justify-center items-end"
                    >
                        <div className={`w-full max-w-7xl bg-white/95 backdrop-blur-xl border border-gray-200 shadow-[0_-10px_40px_rgba(0,0,0,0.15)] rounded-3xl overflow-hidden relative ${isMandatory ? 'ring-4 ring-primary-500/20' : ''}`}>
                            {/* Decorative BG */}
                            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-[hsl(264,50%,98%)] via-white to-white -z-10"></div>

                            {/* Main Content */}
                            <div className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
                                <div className="hidden md:flex bg-gradient-to-br from-primary-100 to-white p-4 rounded-2xl shadow-sm text-primary-500 shrink-0 border border-primary-50">
                                    <Cookie fontSize="large" />
                                </div>

                                <div className="flex-1">
                                    <h3 className="text-xl font-bold mb-2 text-gray-900">
                                        We value your privacy
                                        {isMandatory && <span className="ml-3 text-xs font-normal bg-red-100 text-red-600 px-2 py-1 rounded-full border border-red-200">Action Required</span>}
                                    </h3>
                                    <p className="text-sm text-gray-600 leading-relaxed max-w-4xl">
                                        We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic.
                                        By clicking "Accept All", you consent to our use of cookies. You have the right to withdraw your consent at any time.
                                        For more information on how we handle your data and retention policies, please read our <Link to="/privacy-policy" className="text-primary-600 hover:underline font-semibold">Privacy Policy</Link> and <Link to="/cookies-and-ip" className="text-primary-600 hover:underline font-semibold">Cookie Policy</Link>.
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0 self-start md:self-center">
                                    <button
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className={`px-6 py-3 rounded-xl border font-bold transition-all text-sm flex items-center justify-center gap-2 ${isExpanded ? 'bg-gray-100 border-gray-300 text-gray-900' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                    >
                                        {isExpanded ? <ExpandLess fontSize="small" /> : <Settings fontSize="small" />}
                                        {isExpanded ? 'Hide Options' : 'Customize'}
                                    </button>
                                    <button
                                        onClick={handleAcceptAll}
                                        className="px-8 py-3 rounded-xl bg-primary-500 text-white font-bold hover:bg-primary-600 shadow-lg shadow-primary-500/20 transition-all hover:scale-105 text-sm"
                                    >
                                        Accept All
                                    </button>
                                </div>
                            </div>

                            {/* Expanded Preferences */}
                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="border-t border-gray-100 bg-gray-50/50"
                                    >
                                        <div className="p-6 md:p-8 space-y-4">

                                            {/* Option 1: Essential */}
                                            <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white border border-gray-200 shadow-sm opacity-80">
                                                <div>
                                                    <div className="font-bold text-gray-900 flex items-center gap-2 text-sm md:text-base">Strictly Necessary <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded uppercase tracking-wider border border-gray-200">Required</span></div>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-3xl">
                                                        These cookies are essential for the website to function properly. They ensure basic functionalities and security features of the website, anonymously. Without these cookies, the website cannot function properly.
                                                    </p>
                                                </div>
                                                <div className="relative inline-flex items-center cursor-not-allowed shrink-0">
                                                    <input type="checkbox" checked={true} disabled className="sr-only peer" />
                                                    <div className="w-11 h-6 bg-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                                                </div>
                                            </div>

                                            {/* Option 2: Analytics */}
                                            <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white border border-gray-200 shadow-sm hover:border-primary-200 transition-colors">
                                                <div>
                                                    <div className="font-bold text-gray-900 text-sm md:text-base">Performance & Analytics</div>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-3xl">
                                                        These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. Different data points including your IP address may be used to generate analytics. They help us to know which pages are the most and least popular and see how visitors move around the site.
                                                    </p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                                    <input type="checkbox" checked={preferences.analytics} onChange={() => togglePreference('analytics')} className="sr-only peer" />
                                                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-100 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                                                </label>
                                            </div>

                                            {/* Option 3: Marketing */}
                                            <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white border border-gray-200 shadow-sm hover:border-primary-200 transition-colors">
                                                <div>
                                                    <div className="font-bold text-gray-900 text-sm md:text-base">Marketing & Advertisement</div>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-3xl">
                                                        These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites. They do not store directly personal information, but are based on uniquely identifying your browser and internet device.
                                                    </p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                                    <input type="checkbox" checked={preferences.marketing} onChange={() => togglePreference('marketing')} className="sr-only peer" />
                                                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-100 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                                                </label>
                                            </div>

                                            <div className="flex justify-end pt-4">
                                                <button
                                                    onClick={handleSavePreferences}
                                                    className="px-8 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-black shadow-lg shadow-gray-900/20 transition-all text-sm flex items-center gap-2"
                                                >
                                                    <Check fontSize="small" /> Save My Preferences
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default CookieConsent;
